import dayjs from "dayjs";
import { calculatePastTransactionsBalance } from "./calculateTotals";

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c]);

const fmt = (v) =>
  v === "" || v == null ? "" : Math.round(num(v)).toLocaleString("en-IN");

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });

export const compareTransactions = (a, b) => {
  const dayOf = (t) => dayjs(t?.dateOfTransaction).startOf("day").valueOf();
  const dateDiff = dayOf(a) - dayOf(b);
  if (Number.isFinite(dateDiff) && dateDiff) return dateDiff;

  const createdDiff =
    new Date(a?.createdAt).getTime() - new Date(b?.createdAt).getTime();
  if (Number.isFinite(createdDiff) && createdDiff) return createdDiff;

  return String(a?._id || "").localeCompare(String(b?._id || ""));
};

const labelOf = (r) => {
  if (/^TRY PAYMENT/.test(r.description)) return "Tray Payment";
  if (r.description === "Payment Received") return "Cash / Online Payment";
  return r.description;
};

// Builds ledger lines per transaction, grouped into sections with running dues.
export const buildBalanceSheet = (transactions, allTransactions, trayTypes) => {
  const sorted = [...transactions].sort(compareTransactions);
  const complete = [
    ...(allTransactions?.length > 0 ? allTransactions : transactions),
  ].sort(compareTransactions);

  const first = sorted[0];
  // Sum each earlier transaction on its own so ordering matches this sheet exactly
  const openingBalance = first
    ? complete
        .filter((tx) => compareTransactions(tx, first) < 0)
        .reduce(
          (sum, tx) =>
            sum + calculatePastTransactionsBalance(tx, [tx], trayTypes),
          0
        )
    : 0;

  let balance = openingBalance;
  const groups = sorted.map((tx) => {
    const rows = [];
    const add = ({
      description = "",
      item = "",
      rate = "",
      quantity = "",
      amount = 0,
      payment = 0,
      displayRate = rate,
      displayAmount = amount,
      excluded = false,
    }) => {
      balance += num(amount) - num(payment);
      rows.push({
        description,
        item,
        quantity,
        displayRate,
        displayAmount,
        excluded,
        amount: num(amount),
        payment: num(payment),
        balance,
      });
    };

    (tx.sales || []).forEach((sale) => {
      const saved = Number(sale?.total);
      add({
        description: "Sales",
        item: sale?.itemName,
        rate: sale?.costPerItem ?? "",
        quantity: sale?.numberOfItems ?? "",
        amount:
          sale?.total != null && Number.isFinite(saved)
            ? saved
            : num(sale?.numberOfItems) * num(sale?.costPerItem),
      });
    });

    if (tx.labourCharges?.enabled || num(tx.labourCharges?.total) > 0) {
      add({
        description: "Sales",
        item: "Loading Charges",
        rate: tx.labourCharges?.costPerItem ?? "",
        quantity: tx.labourCharges?.numberOfItems ?? "",
        amount: tx.labourCharges?.total,
      });
    }

    if (num(tx.discount) > 0) {
      add({ description: "Discount", amount: -num(tx.discount) });
    }

    const damageRefunds =
      Array.isArray(tx.damageRefunds) && tx.damageRefunds.length > 0
        ? tx.damageRefunds
        : tx.damageRefund?.enabled || tx.damageRefund?.total
        ? [tx.damageRefund]
        : [];
    damageRefunds.forEach((refund) => {
      add({
        description: "Damage Refund",
        item: refund?.itemName,
        quantity: refund?.numberOfItems ?? "",
        payment: refund?.total,
      });
    });

    (tx.trays || []).forEach((tray) => {
      const trayType = (trayTypes || []).find(
        (t) => t.trayTypeId === tray.trayTypeId || t.id === tray.trayTypeId
      );
      const rate = num(tray.costPerTray) || num(trayType?.price);
      const quantity = num(tray.numberOfTrays);
      const name = trayType?.name || trayType?.label || "";
      add({
        description: `TRY PAYMENT ${name || fmt(rate)}`,
        quantity,
        displayRate: rate,
        displayAmount: quantity * rate,
        excluded: !!tray.excludePayment,
        payment: tray.excludePayment ? 0 : quantity * rate,
      });
    });

    if (num(tx.paymentAmount) > 0) {
      add({ description: "Payment Received", payment: tx.paymentAmount });
    }

    const startBalance = rows[0]
      ? rows[0].balance - (rows[0].amount - rows[0].payment)
      : balance;

    const sections = [];
    rows.forEach((r) => {
      const label = labelOf(r);
      const last = sections[sections.length - 1];
      const value = /^TRY PAYMENT/.test(r.description)
        ? r.excluded
          ? 0
          : r.displayAmount
        : r.payment
        ? r.payment
        : r.amount;
      const net = r.amount - r.payment;
      if (last && last.label === label) {
        last.rows.push(r);
        last.total += value;
        last.net += net;
        last.excluded = last.excluded && !!r.excluded;
      } else {
        sections.push({ label, rows: [r], total: value, net, excluded: !!r.excluded });
      }
    });

    // Due is counted from the first section downwards
    let due = startBalance;
    sections.forEach((s) => {
      due += s.net;
      s.due = due;
    });

    return { tx, sections };
  });

  return {
    groups,
    openingBalance,
    closingBalance: balance,
  };
};

const renderRows = (groups) =>
  groups
    .map(({ tx, sections }) => {
      const rowCount = sections.reduce((n, s) => n + s.rows.length, 0);
      let firstRow = true;
      return sections
        .map((section) =>
          section.rows
            .map((row, idx) => {
              const label = section.label;
              const wide = label === "Discount" || label === "Cash / Online Payment";
              const loading = label === "Loading Charges";
              const isTray = label === "Tray Payment";
              const head = idx === 0;
              const cells = [];
              if (firstRow) {
                cells.push(
                  `<td class="date" rowspan="${rowCount}">${formatDate(tx.dateOfTransaction)}</td>`
                );
                firstRow = false;
              }
              if (head) {
                const notes = label === "Sales" ? tx.salesNotes : label === "Cash / Online Payment" ? tx.paymentNotes : "";
                const notesHtml = notes ? `<div class="section-notes"><strong>Notes:</strong><div>${escapeHtml(notes)}</div></div>` : "";
                const colspan = wide ? 5 : loading ? 2 : 1;
                cells.push(
                  `<td class="label" rowspan="${section.rows.length}" colspan="${colspan}">${escapeHtml(label)}${notesHtml}</td>`
                );
              }
              if (!wide) {
                if (!loading) {
                  const item = isTray
                    ? row.description.replace(/^TRY PAYMENT\s*/, "")
                    : row.item;
                  cells.push(`<td class="text">${escapeHtml(item)}</td>`);
                }
                cells.push(
                  `<td class="numeric">${fmt(row.displayRate)}</td>`,
                  `<td class="numeric">${fmt(row.quantity)}</td>`,
                  `<td class="numeric">${fmt(row.displayAmount)}</td>`
                );
              }
              if (head) {
                cells.push(
                  section.excluded
                    ? `<td class="numeric" rowspan="${section.rows.length}">No Cost</td>`
                    : `<td class="numeric" rowspan="${section.rows.length}">${fmt(section.total)}</td>`,
                  `<td class="numeric balance" rowspan="${section.rows.length}">${fmt(section.due)}</td>`
                );
              }
              return `<tr>${cells.join("")}</tr>`;
            })
            .join("")
        )
        .join("");
    })
    .join("");

export const buildBalanceSheetHtml = ({
  title,
  logo,
  employerName,
  branchName,
  customer,
  transactions,
  allTransactions,
  trayTypes,
}) => {
  const { groups, closingBalance } = buildBalanceSheet(
    transactions,
    allTransactions,
    trayTypes
  );
  const due = Math.round(closingBalance || 0);
  const dueText = `${due < 0 ? "-" : ""}₹${Math.abs(due).toLocaleString("en-IN")}`;

  return `
  <!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>${escapeHtml(title)}</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 30px; color: #333; }
        .header { display: flex; justify-content: space-between; border-bottom: 3px solid #1976d2; padding-bottom: 10px; margin-bottom: 20px; }
        .proprietor-meta { font-size: 13px; color: #555; margin-top: 4px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th { background: #e6e6e6; font-weight: bold; border: 1px solid #ccc; padding: 6px; }
        td { border: 1px solid #ccc; padding: 5px 6px; vertical-align: middle; }
        td.date { font-weight: 700; vertical-align: top; }
        .section-notes { margin-top: 6px; font-weight: 400; max-width: 240px; }
        .section-notes div { white-space: pre-wrap; overflow-wrap: anywhere; }
        td.label { font-weight: 700; }
        td.text { text-align: left; }
        td.numeric { text-align: right; white-space: nowrap; }
        td.balance { background: #f1f1f1 !important; font-weight: 700; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        tr { page-break-inside: avoid; }
      </style>
    </head>
    <body>
      <div class="header">
        <div style="display: flex; align-items: center; gap: 12px;">
          <img src="${logo}" style="width: 50px; height: 50px; border-radius: 4px;" />
          <div>
            <div style="font-size: 20px; font-weight: bold; color: #1976d2;">${escapeHtml(employerName || "")}</div>
            <div style="color: #666; font-size: 16px;">${escapeHtml(branchName || "Transaction Receipt")}</div>
            <div class="proprietor-meta">
              Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong>
            </div>
          </div>
        </div>
      </div>

      <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; padding: 4px 0;">
        <div>
          <div style="font-size: 18px; font-weight: bold; text-transform: capitalize; color: #222;">${escapeHtml(customer?.name || "")}</div>
          <div style="color: #666; font-size: 13px; margin-top: 4px;">${escapeHtml(customer?.mobile || "")}${customer?.mobile && customer?.location ? " • " : ""}${escapeHtml(customer?.location || "")}</div>
        </div>
        <div style="text-align: right;">
          <div style="font-size: 12px; color: #666; text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px;">Total Balance Due</div>
          <div style="font-size: 28px; font-weight: 800; color: #1976d2; margin-top: 2px; line-height: 1.2;">${dueText}</div>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Item</th>
            <th>Rate</th>
            <th>Qty<br />(Box)</th>
            <th>Amount</th>
            <th>Total</th>
            <th>Total Due</th>
          </tr>
        </thead>
        <tbody>${renderRows(groups)}</tbody>
      </table>
    </body>
  </html>`;
};
